import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest, filter, firstValueFrom } from 'rxjs';

import { ButtonType } from '@shared/enums/button-type';
import { EIcon } from '@shared/enums/icon.enum';
import { ConfirmationModalData } from '@shared/interfaces/modal-data';
import { TemplateEditorAreaComponent } from '@template/components/template-editor-dialog/template-editor-area/template-editor-area.component';
import { TemplateEditorBaseDnd } from '@template/components/template-editor-dialog/template-editor-base/template-editor-base-dnd.component';
import { TemplateEditorHeaderComponent } from '@template/components/template-editor-dialog/template-editor-header/template-editor-header';
import { RestTemplateModel } from '@template/models/rest-template-model.interface';
import { AreaModel, AreaPageModel, TemplateEditorContent } from '@template/models/template-editor-model';
import { TemplateEditorService } from '@template/services/template-editor.service';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';

export type TemplateEditorDialogData = {
  templateId: number;
  highLightHeader?: boolean;
};

export interface TemplateEditorDialogResult {
  inspectionTemplateId?: number;
  inspectionTemplateName?: string;
}

@UntilDestroy()
@Component({
  selector: 'app-template-editor-dialog',
  templateUrl: './template-editor-dialog.component.html',
  styleUrls: ['./template-editor-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateEditorDialogComponent extends TemplateEditorBaseDnd implements OnInit, AfterViewInit {
  ButtonType = ButtonType;
  EIcon = EIcon;

  inspectionTemplateContent: TemplateEditorContent = new TemplateEditorContent();
  isBlockDraggable = false;

  @ViewChild('endListPlaceholder', { read: ElementRef, static: false })
  endListPlaceholder: ElementRef<HTMLElement>;
  isLoading = true;
  isItemsLoading = true;
  inspectionTemplate: RestTemplateModel;

  @ViewChildren(TemplateEditorAreaComponent) inspectionItems!: QueryList<TemplateEditorAreaComponent>;
  @ViewChild(TemplateEditorHeaderComponent) header: TemplateEditorHeaderComponent;

  constructor(
    public dialogRef: MatDialogRef<TemplateEditorDialogComponent, TemplateEditorDialogResult>,
    store: Store<{}>,
    protected cdr: ChangeDetectorRef,
    renderer: Renderer2,
    private templateEditorService: TemplateEditorService,
    protected snackbarService: SnackbarService,
    @Inject(MAT_DIALOG_DATA) public data: TemplateEditorDialogData
  ) {
    super(store, snackbarService, renderer, cdr);
  }

  ngOnInit(): void {
    combineLatest([this.templateEditorService.getTemplate(this.data.templateId)])
      .pipe(untilDestroyed(this))
      .subscribe(([inspectionTemplate]) => {
        this.inspectionTemplate = inspectionTemplate;

        this.inspectionTemplateContent.init(inspectionTemplate);
        // this.inspectionTemplateContent.itemsChanges$.subscribe(() => {
        //   this.customInspectionService
        //     .updateCustomInspectionTemplateContent(this.data.templateId, this.inspectionTemplateContent.templateItems)
        //     .pipe(untilDestroyed(this))
        //     .subscribe();
        // });
        this.isLoading = false;
        this.isItemsLoading = false;
        this.cdr.markForCheck();
      });

    this.dialogRef.backdropClick().subscribe(() => {
      this.cancel();
    });

    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.cancel();
      }
    });
  }

  async add() {
    const areaMaxNumber = this.inspectionTemplateContent.areas
      .filter(e => /^Area [0-9]*$/.test(e.title))
      .map(c => +c.title.replace('Area ', ''))
      .reduce((prev, max) => (prev > max ? prev : max), 0);

    const [id, area] = await firstValueFrom(
      this.templateEditorService.addArea(`Area ${areaMaxNumber + 1}`, this.inspectionTemplate.id)
    );
    this.inspectionTemplateContent.addArea(area);
    this.cdr.markForCheck();
    setTimeout(() => {
      const item = this.inspectionItems.find(c => c.id === id);
      item?.enterEditMode();
      this.endListPlaceholder.nativeElement.scrollIntoView();
    }, 100);
  }

  enterPressed(item: AreaModel) {
    const index = this.inspectionTemplateContent.areas.findIndex(itemInner => itemInner.id === item.id);
    if (index === this.inspectionTemplateContent.areas.length - 1) {
      this.add();
    } else {
      const id = this.inspectionTemplateContent.areas[index + 1].id;
      const selectedItem = this.inspectionItems.find(c => c.id === id);
      selectedItem?.enterEditMode();
    }
  }

  dropArea(data: AreaModel[]) {
    super.drop(data);
    this.inspectionTemplateContent.onAreasChanged();
    this.templateEditorService.updateAreas(this.inspectionTemplateContent.areas).subscribe();
  }

  remove(item: AreaModel) {
    if (!this.checkEditRight()) {
      return;
    }
    this.templateEditorService.removeArea(item.id).subscribe();
    this.inspectionTemplateContent.removeArea(item.id);
  }

  areaChange(item: AreaModel) {
    if (!this.checkEditRight()) {
      return;
    }
    this.templateEditorService.updateArea(item).subscribe();
    this.inspectionTemplateContent.updateArea(item);
  }

  removeTemplate() {
    if (!this.checkEditRight()) {
      return;
    }
    const data: ConfirmationModalData = {};
    this.templateEditorService
      .openRemovalTemplateModal(this.inspectionTemplate, data)
      .afterClosed()
      .pipe(filter(result => result === DialogResult.Success))
      .subscribe(() => {
        this.dialogRef.close();
      });
  }

  headerChange(header: string) {
    this.inspectionTemplate.name = header;

    this.updateCurrentTemplate();
  }

  updateCurrentTemplate() {
    this.templateEditorService
      .editTemplate(this.inspectionTemplate.id, {
        inspectionName: this.inspectionTemplate.name,
        isRequired: this.inspectionTemplate.customInspectionIsRequired,
      })
      .subscribe();
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    if (this.data.highLightHeader) {
      setTimeout(() => {
        this.header?.enterEditMode();
      }, 0);
    }
  }

  cancel() {
    this.dialogRef.close();
  }
}
